import { Injectable, Injector } from '@angular/core';
import { Router, RoutesRecognized } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { BehaviorSubject, filter, pairwise } from 'rxjs';

import { environment } from '../../environments/environment';

import {
  ActivityReportsCountersModel,
  AssignmentsCounterResponseModel,
  BannerModel,
  EBSCounterResponseModel,
  HeaderFiltersModel,
  HeaderTogglersModel,
  InvoiceCountersResponseModel,
  MileageMoneyCountersModel,
  PillModel,
  ReferralsCountersModel,
  SidebarRouteExtended,
  SortingOptionModel,
  TogglerModel,
  VacationRequestsCountersModel,
  ContractExtensionsCountersModel,
  SecondarySearch,
  HeaderDropdownsModel,
  CustomPagesModel
} from '@shared/models';

import { AutoReloadService, SidebarStorageService, UserService } from '@shared/services';

const RECENT_PILLS_TABLE_NAME     = 'recentPills';
const PASSED_TUTORIALS_TABLE_NAME = 'passedTutorials';
const PASSED_RELEASES_TABLE_NAME  = 'passedReleases';

@Injectable({
  providedIn: 'root'
})
export class SessionStorageService {
  private activityReportsArchiveFormValues:  object;
  private photoDocumentsArchiveFormValues:   object;
  private ebsArchiveFormValues:              object;
  private vacationRequestsArchiveFormValues: object;
  private mileageMoneyArchiveFormValues:     object;
  private contractExtensionsFormValue:       object;
  private referralsArchiveFormValues:        object;
  private failedERPDateFiltersValues:        object;
  private reloadAvailableState:              boolean;

  private activeAppSource = new BehaviorSubject<string>('time-tracking');
  activeApp = this.activeAppSource.asObservable();

  private activeTabSource = new BehaviorSubject<SidebarRouteExtended>(null);
  activeTab = this.activeTabSource.asObservable();

  private itemsPerPageSource = new BehaviorSubject<number>(10);
  private pageNumberSource   = new BehaviorSubject<number>(1);
  private totalCountSource   = new BehaviorSubject<number>(1);
  private totalPagesSource   = new BehaviorSubject<number>(1);
  private customPagesSource  = new BehaviorSubject<CustomPagesModel>({});

  itemsPerPage = this.itemsPerPageSource.asObservable();
  pageNumber   = this.pageNumberSource.asObservable();
  totalCount   = this.totalCountSource.asObservable();
  totalPages   = this.totalPagesSource.asObservable();
  customPages  = this.customPagesSource.asObservable();

  private sortingFlowSource = new BehaviorSubject<SortingOptionModel[]>(null);
  sortingFlow = this.sortingFlowSource.asObservable();

  private searchValueSource          = new BehaviorSubject<string>('');
  private searchSecondaryValueSource = new BehaviorSubject<SecondarySearch>(null);
  searchValue          = this.searchValueSource.asObservable();
  searchSecondaryValue = this.searchSecondaryValueSource.asObservable();

  private bannersSource = new BehaviorSubject<BannerModel[]>(null);
  banners = this.bannersSource.asObservable();

  private pillsSource = new BehaviorSubject<PillModel[]>(null);
  pills = this.pillsSource.asObservable();

  private dynamicComponentSource = new BehaviorSubject<any>(null);
  dynamicComponent = this.dynamicComponentSource.asObservable();

  private mobileGoBackFuncSource = new BehaviorSubject<Function>(null);
  mobileGoBackFunc = this.mobileGoBackFuncSource.asObservable();

  private activityReportsCountersTokensSource       = new BehaviorSubject<any>(null);
  private photoDocumentsCountersTokensSource        = new BehaviorSubject<any>(null);
  private referralsCountersTokensSource             = new BehaviorSubject<any>(null);
  private contractExtensionsCountersTokensSource    = new BehaviorSubject<any>(null);
  private ebsCountersTokensSource                   = new BehaviorSubject<any>(null);
  private assignmentsCountersTokensSource           = new BehaviorSubject<any>(null);
  private invoicesCountersTokensSource              = new BehaviorSubject<any>(null);
  private vacationRequestsCountersTokensSource      = new BehaviorSubject<any>(null);
  private mileageMileageReportsCountersTokensSource = new BehaviorSubject<any>(null);
  private externalEmployeesCountersTokensSource     = new BehaviorSubject<any>(null);

  private headerFiltersSource = new BehaviorSubject<HeaderFiltersModel>({
    ebs: [
      { label: 'Bestätigt',   field: 'ebsConfirmed',   icon: 'icon-circle-tick'                },
      { label: 'Unbestätigt', field: 'ebsUnConfirmed', icon: 'icon-circle-cross', active: true },
    ],
    failed_erp: [
      { label: 'Offen',    field: 'zvooveImportFailed',   icon: 'icon-circle-cross', active: true },
      { label: 'Erledigt', field: 'zvooveImportResolved', icon: 'icon-circle-tick'                },
    ]
  });

  private headerTogglersSource = new BehaviorSubject<HeaderTogglersModel>({
    wp_details: [
      { label: 'Echtzeit',        value: 'real',      active: true },
      { label: 'Industriezeit',   value: 'industrial'              }
    ],
    // vacations: [
    //   { label: 'Klärungsbedarf',  value: 'rejected', icon: 'icon-circle-cross color-red',    isInternal: true },
    //   { label: 'Freigegeben',     value: 'approved', icon: 'icon-circle-tick  color-green',  isInternal: true },
    //   { label: 'Ausstehend',      value: 'open',     icon: 'icon-circle-time  color-orange', isInternal: true },

    //   { label: 'Offen',           value: 'open',     icon: 'icon-circle-time  color-orange', isCustomer: true },
    //   { label: 'Nicht bestätigt', value: 'rejected', icon: 'icon-circle-cross color-red',    isCustomer: true },
    //   { label: 'Bestätigt',       value: 'approved', icon: 'icon-circle-tick  color-green',  isCustomer: true }
    // ],
    invoice: [
      { label: 'Dashboard',       value: 'dashboard' },
      { label: 'Archiv',          value: 'archive'   }
    ],
    failed_erp: [
      { label: 'TN', value: 'ar', icon: 'icon-time-tracking', active: true              },
      { label: 'UA', value: 'vr', icon: 'icon-holiday',       beta: 'vacation_requests' }
    ],
    archive: [
      { label: 'Tätigkeitsnachweis',    labelMin: 'TN',  value: 'ar',  icon: 'icon-time-tracking', active:     true                   },
      { label: 'Urlaubsanträge',        labelMin: 'UA',  value: 'vr',  icon: 'icon-holiday',       beta: 'vacation_requests'          },
      { label: 'Einsatzverlängerungen', labelMin: 'EXT', value: 'ext', icon: 'icon-note',          isInternal: true                   },
      { label: 'Fotodokument',          labelMin: 'FD',  value: 'pd',  icon: 'icon-picture',       isInternal: true                   },
      { label: 'Einsatzbegleitscheine', labelMin: 'EBS', value: 'ebs', icon: 'icon-user',          isInternal: true                   },
      { label: 'MA werben MA',          labelMin: 'REF', value: 'ref', icon: 'icon-tempton-jet',   isInternal: true                   },
      { label: 'Kilometergeldberichte', labelMin: 'KG',  value: 'mm',  icon: 'icon-car',           isInternal: true,
                                                                                                   beta: 'standalone_mileage_reports' },
    ]
  });

  private headerDropdownsSource = new BehaviorSubject<HeaderDropdownsModel>({
    ee_list: [
      { icon: 'icon-case', label: 'Ausgetreten', field: 'employement', value: null, options: [{ label: 'Alle', value: null }, { label: 'Ja', value: 'inactive'  }, { label: 'Nein', value: 'active'        }]},
      { icon: 'icon-user', label: 'Aktiviert',   field: 'activated',   value: null, options: [{ label: 'Alle', value: null }, { label: 'Ja', value: 'activated' }, { label: 'Nein', value: 'not_activated' }]},
    ]
  });

  headerFilters   = this.headerFiltersSource.asObservable();
  headerTogglers  = this.headerTogglersSource.asObservable();
  headerDropdowns = this.headerDropdownsSource.asObservable();

  buildHashValue:    any;
  passedTutorials:   any;

  prevUrl:           string;
  currentUrl:        string;
  redirectUrl:       string;
  eeRedirectUrl:     string;

  latestCreatedByMe: number[];
  failedResolved:    string;

  private chatFullAccessSource = new BehaviorSubject<boolean>(null);
  chatFullAccess = this.chatFullAccessSource.asObservable();
  constructor (
    private injector:              Injector,
    private router:                Router,
    private titleService:          Title,
    private userService:           UserService,
    private sidebarStorageService: SidebarStorageService
  ) { }

  get activityReportsArchiveForm():  object  { return this.activityReportsArchiveFormValues;  }
  get photoDocumentsArchiveForm():   object  { return this.photoDocumentsArchiveFormValues;   }
  get ebsArchiveForm():              object  { return this.ebsArchiveFormValues;              }
  get vacationRequestsArchiveForm(): object  { return this.vacationRequestsArchiveFormValues; }
  get mileageMoneyArchiveForm():     object  { return this.mileageMoneyArchiveFormValues;     }
  get contractExtensionsForm():      object  { return this.contractExtensionsFormValue;       }
  get referralsArchiveForm():        object  { return this.referralsArchiveFormValues;        }
  get failedERPDateFilters():        object  { return this.failedERPDateFiltersValues;        }
  get reloadAvailable():             boolean { return this.reloadAvailableState;              }
  get buildHash():                   object  { return this.buildHashValue;                    }

  set activityReportsArchiveForm( data:  object ) { this.activityReportsArchiveFormValues  = data;  }
  set photoDocumentsArchiveForm(  data:  object ) { this.photoDocumentsArchiveFormValues   = data;  }
  set ebsArchiveForm(             data:  object ) { this.ebsArchiveFormValues              = data;  }
  set vacationRequestsArchiveForm(data:  object ) { this.vacationRequestsArchiveFormValues = data;  }
  set mileageMoneyArchiveForm(    data:  object ) { this.mileageMoneyArchiveFormValues     = data;  }
  set contractExtensionsForm(     data:  object ) { this.contractExtensionsFormValue       = data;  }
  set referralsArchiveForm(       data:  object ) { this.referralsArchiveFormValues        = data;  }
  set failedERPDateFilters(       data:  object ) { this.failedERPDateFiltersValues        = data;  }
  set reloadAvailable(            state: boolean) { this.reloadAvailableState              = state; }
  set buildHash(                  data:  object ) { this.buildHashValue                    = data;  }

  changeAndResetActiveTab(identifier: string): void {
    let filterItem = this.findSidebarItem(identifier);
    this.changeItemsPerPage(filterItem?.paginationNumber || 10);
    this.changePageNumber(1);
    this.changeSortingFlow([{
      columns: filterItem.sortByDefault      || ['created_at'],
      order:   filterItem.sortOrderByDefault || [false]
    }]);
    this.changeSearchValue('');
    this.changeCustomPages();
    this.collapseSidebarSection(filterItem);
    this.changeActiveTab(identifier);
    this.redirectUrl   = null;
    this.eeRedirectUrl = null;
  }

  changeActiveTab(identifier: string): void {
    let filterItem = this.findSidebarItem(identifier);
    this.titleService.setTitle(filterItem.title);
    this.activeTabSource.next(filterItem);
  }

  private findSidebarItem(identifier: string): SidebarRouteExtended {
    let items      = this.sidebarStorageService.sidebarItems.filter(i => i.identifier);
    let children   = this.sidebarStorageService.sidebarItems.filter(i => i.children).reduce((sum, i) => {
      let children = [...i.children];
      children.forEach(c => c.parent = i.identifier);
      sum = [...sum, ...children];
      return sum
    }, []);
    return [...items, ...children].find(item => item.identifier === identifier);
  }

  private collapseSidebarSection(item: SidebarRouteExtended): void {
    if (item.parent) {
      let parent = this.findSidebarItem(item.parent);
      parent.collapse = true;
    }
  }

  get activeAppValue(): string {
    return this.activeAppSource.value;
  }

  setActiveApp(app: string = null): void {
    if (app) this.changeActiveApp(app);
    else if (this.router.url.includes(`/invoices/`)) this.changeActiveApp('invoices');
    else this.changeActiveApp('time-tracking');
  }

  changeActiveApp(app: string): void {
    let autoReloadService = this.injector.get(AutoReloadService);
    setTimeout(() => autoReloadService.resetAutoReload(app));
    this.sidebarStorageService.changeSidebar(app);
    this.activeAppSource.next(app);
    this.userService.setPills(this.userService.currentUserValue);
  }

  get isTimeTracking(): boolean {
    return this.activeAppSource.value === 'time-tracking';
  }

  get isInvoices(): boolean {
    return this.activeAppSource.value === 'invoices';
  }

  get activeTabValue(): SidebarRouteExtended {
    return this.activeTabSource.value;
  }

  get itemsPerPageValue(): number { return this.itemsPerPageSource.value; }
  get pageNumberValue():   number { return this.pageNumberSource.value;   }
  get pageTotalCount():    number { return this.totalCountSource.value;   }
  get pagesTotal():        number { return this.totalPagesSource.value;   }

  get customPagesValue(): CustomPagesModel { return this.customPagesSource.value; }

  changeItemsPerPage(number: number): void { this.itemsPerPageSource.next(number); }
  changePageNumber(  number: number): void { this.pageNumberSource.next(number);   }
  changeTotalCount(  number: number): void { this.totalCountSource.next(number);   }
  changeTotalPages(  number: number): void { this.totalPagesSource.next(number);   }

  changeCustomPages(useCase?: string, page?: number): void {
    if (!useCase) this.customPagesSource.next({});
    else this.customPagesSource.next({ ...this.customPagesValue, [useCase]: page });
  }

  validateCustomPagesEE(eeId: number): void {
    if (this.customPagesValue.eeId !== eeId) this.customPagesSource.next({ eeId });
  }

  changeSortingFlow(sorts: SortingOptionModel[]): void {
    this.sortingFlowSource.next(sorts);
  }

  get sortingFlowValue(): SortingOptionModel[] {
    return this.sortingFlowSource.value;
  }

  get searchValueValue():  string        { return this.searchValueSource.value; }
  changeSearchValue(query: string): void { this.searchValueSource.next(query);  }

  get searchSecondaryValueValue():  SecondarySearch         { return this.searchSecondaryValueSource.value; }
  changeSearchSecondaryValue(search: SecondarySearch): void { this.searchSecondaryValueSource.next(search); }

  changeBanners(banners: BannerModel[]): void {
    this.bannersSource.next(banners);
  }

  setActiveCompany(pill: PillModel): void {
    let pills = this.pillsValue.map(p => {
      if (p.label === pill.label) {
        p.active    = true;
        p.favourite = true;
      }
      else p.active = false;
      return p;
    });

    this.updatePills(pills);
  }

  preparePills(pills: PillModel[]): void {
    const local: PillModel[] = this.loadPillsLocalTable();
    const newPills = pills.map((pill,idx) => {
      const saved     = local.find(l => l.label === pill.label);
      const active    = saved ? saved.active    : pill.active;
      const favourite = saved ? saved.favourite : pill.favourite;
      return {
        ...pill,
        active,
        favourite
      };
    });
    this.updatePills(newPills);
  }

  updatePills(pills: PillModel[]): void {
    this.updatePillsLocalTable(pills);
    this.pillsSource.next(pills);
  }

  private loadPillsLocalTable(): PillModel[] {
    let table = this.parceLocalTable(RECENT_PILLS_TABLE_NAME) || {};
    return table[this.userService.currentUserValue.id] || [];
  }

  private updatePillsLocalTable(pills: PillModel[]): void {
    let table = this.parceLocalTable(RECENT_PILLS_TABLE_NAME);
    table[this.userService.currentUserValue.id] = pills;
    this.updateLocalTable(RECENT_PILLS_TABLE_NAME, table);
  }

  get pillsValue():    PillModel[] { return this.pillsSource.value;                       }
  get activePills():   PillModel[] { return this.pillsSource.value.filter(p => p.active); }
  get getActivePill(): PillModel   { return this.pillsSource.value.find(p => p.active);   }

  addPill(   pill): void { this.setPill(pill, true);  }
  removePill(pill): void { this.setPill(pill, false); }
  togglePill(pill): void { this.setPill(pill);        }

  private setPill(pill: PillModel, state = null): void {
    let localPill = this.pillsValue.find(p => p.label === pill.label);
    if (localPill) localPill.active = state !== null ? state : !localPill.active;
    this.updatePills(this.pillsValue);
  }

  changeDynamicComponents(     comp: any):    void {        this.dynamicComponentSource.next(comp);                                    }
  pushDynamicComponent(        item: any):    void { return this.dynamicComponentSource.next(Object.assign(item, { type: 'create' })); }
  popDynamicComponent(         item: string): void { return this.dynamicComponentSource.next({  component: item,   type: 'remove' });  }
  updateDynamicComponentValues(item: any):    void { return this.dynamicComponentSource.next(Object.assign(item, { type: 'update' })); }

  get headerFiltersValue(): any {
    let filters = {...this.headerFiltersSource.value};
    for (const key in filters) {
      filters[key] = filters[key].reduce((sum, val): any => {
        sum[val.field] = val.active;
        return sum;
      }, {});
    }
    return filters;
  }

  changeEBSFilters(      field: string): void { this.changeHeaderFilters('ebs',        field); }
  changeFailedERPFilters(field: string): void { this.changeHeaderFilters('failed_erp', field); }

  private changeHeaderFilters(useCase: string, field: string): void {
    let filters = this.headerFiltersSource.value;
    filters[useCase].map(filter => {
      if (filter.field === field) filter.active = !filter.active;
      return filter;
    });

    this.changePageNumber(1);
    this.changeTotalPages(1);
    this.headerFiltersSource.next(filters);
  }

  get headerTogglersValue():  HeaderTogglersModel  { return this.headerTogglersSource.value;  }
  get headerDropdownsValue(): HeaderDropdownsModel { return this.headerDropdownsSource.value; }

  changeWpDetailsTogglers(value: string): void { this.changeHeaderTogglers('wp_details', value);             }
  changeInvoiceTogglers(  value: string): void { this.changeHeaderTogglers('invoice',    value, true);       }
  changeFailedERPTogglers(value: string): void { this.changeHeaderTogglers('failed_erp', value, true);       }
  changeArchiveTogglers(  value: string): void { this.changeHeaderTogglers('archive',    value);             }
  changeVacationsTogglers(value: string): void { this.changeHeaderTogglers('vacations',  value, true, true); }
  changeEEListDropdowns(  data: any    ): void { this.changeDropdowns(     'ee_list',    data);              }

  private changeHeaderTogglers(useCase: string, value: string, update: boolean = null, allowToDisable: boolean = null): void {
    let togglers: HeaderTogglersModel = this.headerTogglersSource.value;
    togglers[useCase].forEach((toggler: TogglerModel)  => {
      if (toggler.value === value) toggler.active = allowToDisable ? !toggler.active : true;
      else toggler.active = false;
    });

    this.togglersCallback(togglers, update);
  }

  private changeDropdowns(useCase: string, { field, value }: { field: string, value: string }): void {
    let dropdowns = this.headerDropdownsSource.value;
    let dropdown  = dropdowns[useCase].find(t => t.field === field);

    if (value !== dropdown.value) {
      dropdown.value = value;
      this.changePageNumber(1);
      this.changeTotalPages(1);
      this.headerDropdownsSource.next(dropdowns);
    }
  }

  private togglersCallback(togglers: HeaderTogglersModel, update: boolean = null): void {
    if (update) {
      this.changePageNumber(1);
      this.changeTotalPages(1);
    }
    this.headerTogglersSource.next(togglers);
  }

  get activityReportsCountersTokensValue():     ActivityReportsCountersModel    { return this.activityReportsCountersTokensSource.value;       }
  get photoDocumentsCountersTokensValue():      any                             { return this.photoDocumentsCountersTokensSource.value;        }
  get EBSCountersTokensValue():                 EBSCounterResponseModel         { return this.ebsCountersTokensSource.value;                   }
  get referralsCountersTokensValue():           ReferralsCountersModel          { return this.referralsCountersTokensSource.value;             }
  get assignmentsCountersTokensValue():         AssignmentsCounterResponseModel { return this.assignmentsCountersTokensSource.value;           }
  get invoicesCountersTokensValue():            InvoiceCountersResponseModel    { return this.invoicesCountersTokensSource.value;              }
  get mileageMoneyReportsCountersTokensValue(): MileageMoneyCountersModel       { return this.mileageMileageReportsCountersTokensSource.value; }
  get vacationRequestsCountersTokensValue():    VacationRequestsCountersModel   { return this.vacationRequestsCountersTokensSource.value;      }
  get contractExtensionsCountersTokensValue():  ContractExtensionsCountersModel { return this.contractExtensionsCountersTokensSource.value;    }
  get externalEmployeesCountersTokensValue():   ContractExtensionsCountersModel { return this.externalEmployeesCountersTokensSource.value;     }

  changeActivityReportsCountersTokens(tokens:     ActivityReportsCountersModel):    void { this.activityReportsCountersTokensSource.next(tokens);       }
  changePhotoDocumentsCountersTokens(tokens:      any):                             void { this.photoDocumentsCountersTokensSource.next(tokens);        }
  changeEBSCountersTokens(tokens:                 EBSCounterResponseModel):         void { this.ebsCountersTokensSource.next(tokens);                   }
  changeReferralsCountersTokens(tokens:           ReferralsCountersModel):          void { this.referralsCountersTokensSource.next(tokens);             }
  changeAssignmentsCountersTokens(tokens:         AssignmentsCounterResponseModel): void { this.assignmentsCountersTokensSource.next(tokens);           }
  changeInvoicesCountersTokens(tokens:            InvoiceCountersResponseModel):    void { this.invoicesCountersTokensSource.next(tokens);              }
  changeMileageMoneyReportsCountersTokens(tokens: MileageMoneyCountersModel):       void { this.mileageMileageReportsCountersTokensSource.next(tokens); }
  changeVacationRequestsCountersTokens(tokens:    VacationRequestsCountersModel):   void { this.vacationRequestsCountersTokensSource.next(tokens);      }
  changeContractExtensionsCountersTokens(tokens:  ContractExtensionsCountersModel): void { this.contractExtensionsCountersTokensSource.next(tokens);    }
  changeExternalEmployeesCountersTokens(tokens:   ContractExtensionsCountersModel): void { this.externalEmployeesCountersTokensSource.next(tokens);     }

  counterByName(name: string): [any,(tokens: any) => void] {
    switch (name) {
      case 'activityReports':     return [ this.activityReportsCountersTokensValue     , this.changeActivityReportsCountersTokens.bind(this)     ];
      case 'photoDocuments':      return [ this.photoDocumentsCountersTokensValue      , this.changePhotoDocumentsCountersTokens.bind(this)      ];
      case 'EBS':                 return [ this.EBSCountersTokensValue                 , this.changeEBSCountersTokens.bind(this)                 ];
      case 'referrals':           return [ this.referralsCountersTokensValue           , this.changeReferralsCountersTokens.bind(this)           ];
      case 'assignments':         return [ this.assignmentsCountersTokensValue         , this.changeAssignmentsCountersTokens.bind(this)         ];
      case 'invoices':            return [ this.invoicesCountersTokensValue            , this.changeInvoicesCountersTokens.bind(this)            ];
      case 'mileageMoneyReports': return [ this.mileageMoneyReportsCountersTokensValue , this.changeMileageMoneyReportsCountersTokens.bind(this) ];
      case 'vacationRequests':    return [ this.vacationRequestsCountersTokensValue    , this.changeVacationRequestsCountersTokens.bind(this)    ];
      case 'contract-ext':        return [ this.contractExtensionsCountersTokensValue  , this.changeContractExtensionsCountersTokens.bind(this)  ];
      case 'externals':           return [ this.externalEmployeesCountersTokensValue   , this.changeExternalEmployeesCountersTokens.bind(this)   ];
      default:
        throw new Error(`Unknown counter name: ${name}`);
    }
  }

  prepareTutorialsPassedData(): void {
    let passedTutorialsData = {};
    let passedTutorialsList = this.getLocalTableDataByUser(PASSED_TUTORIALS_TABLE_NAME) || [];
    passedTutorialsList.forEach((t: string) => passedTutorialsData[this.normalizeName(t)] = true);

    this.passedTutorials = passedTutorialsData;
  }

  checkTutorialPassed(tutorial: string): boolean {
    let passedTutorialsList = this.getLocalTableDataByUser(PASSED_TUTORIALS_TABLE_NAME) || [];
    return !!passedTutorialsList.find(t => t === tutorial);
  }

  setPassedTutorial(tutorial: string): void {
    this.updateLocalTableAndSave(PASSED_TUTORIALS_TABLE_NAME, tutorial);
    if (tutorial === 'mitarbeiter') this.setActiveApp();
  }

  showTutorial(tutorial: string): void {
    if (!this.passedTutorials[this.normalizeName(tutorial)]) {
      this.passedTutorials[this.normalizeName(tutorial)] = true;
      this.pushDynamicComponent({
        component: 'Tutorial',
        props: { tutorial }
      });
    }
  }

  checkReleasePassed(release) {
    return this.getLocalTableDataByUser(PASSED_RELEASES_TABLE_NAME).includes(`${release.application}_${release.released_at}_${release.id}`);
  }

  setPassedRelease(): void {
    let release, lastRelease = this.userService.latestRelease;
    let table  = this.parceLocalTable(PASSED_RELEASES_TABLE_NAME);
    let passed = this.getLocalTableDataByUser(PASSED_RELEASES_TABLE_NAME);
    if (lastRelease) release = `${lastRelease.application}_${lastRelease.released_at}_${lastRelease.id}`;
    if (release && !passed.find(p => p === release)) table[this.env][this.userService.currentUserValue.id].push(release);
    this.updateLocalTable(PASSED_RELEASES_TABLE_NAME, table);
    this.setActiveApp();
  }

  private updateLocalTableAndSave(tableName: string, value: string): void {
    let passed = this.parceLocalTable(tableName);
    passed[this.env][this.userService.currentUserValue.id].push(value);
    this.updateLocalTable(tableName, passed);
  }

  private getLocalTableDataByUser(table:string): any[] {
    let temp = this.parceLocalTable(table);
    if (!temp[this.env]) temp[this.env] = {};
    if (!temp[this.env][this.userService.currentUserValue.id]) {
      temp[this.env][this.userService.currentUserValue.id] = [];
      this.updateLocalTable(table, temp);
    }
    return temp[this.env][this.userService.currentUserValue.id];
  }

  private parceLocalTable(table: string): unknown {
    return localStorage.getItem(table) && JSON.parse(localStorage.getItem(table)) || {};
  }

  private updateLocalTable(table: string, data: unknown): void {
    localStorage.setItem(table, JSON.stringify(data));
  }

  get env(): string {
    return environment.envName;
  }

  get production(): boolean {
    return this.env === 'live';
  }

  get usersSettings(): any {
    let usersSettings = JSON.parse(localStorage.getItem('usersSettings')) || {};
    localStorage.setItem('usersSettings', JSON.stringify(usersSettings));
    return usersSettings;
  }

  get currentUserSettings(): any {
    let userSettings = this.usersSettings;
    let userId = this.userService.currentUserValue.id;
    if (!userSettings[userId]) userSettings[userId] = {};
    localStorage.setItem('usersSettings', JSON.stringify(userSettings));
    return userSettings[userId];
  }

  get printOptions() {
    if (this.currentUserSettings.printOptions) this.currentUserSettings.printOptions = {};
    return this.currentUserSettings.printOptions;
  }

  updateCurrentUserSettings(val: any) {
    let userSettings = this.usersSettings;
    userSettings[this.userService.currentUserValue.id] = val;
    localStorage.setItem('usersSettings', JSON.stringify(userSettings));
  }

  updateUserSettings(value, ...path): void {
    this.updateCurrentUserSettings(this.updateUserSettingField(this.currentUserSettings, value, ...path));
  }

  private updateUserSettingField(obj, value, ...path): any {
    if (!obj[path[0]]) obj[path[0]] = {};
    if (path.length > 1) obj[path[0]] = this.updateUserSettingField(obj[path[0]], value, ...path.slice(1));
    else obj[path[0]] = value;
    return obj;
  }

  watchRoutes(): void {
    this.router.events.pipe(filter((event: any) => event instanceof RoutesRecognized), pairwise())
    .subscribe((events: RoutesRecognized[]) => {
      this.prevUrl    = events[0].urlAfterRedirects;
      this.currentUrl = events[1].urlAfterRedirects;
    });
  }

  normalizeName(field: string, postValue: string = null): string {
    let temp  = field.split('-');
    let tempL = temp[0];
    let tempU = temp.slice(1).map(t => this.capitalize(t));
    return `${tempL}${tempU}${postValue}`;
  }

  private capitalize(string: string): string {
    return `${string.charAt(0).toUpperCase()}${string.slice(1)}`;
  }

  setMobileGoBackFunc(callback: Function): void {
    this.mobileGoBackFuncSource.next(callback);
  }

  getMobileGoBackFunc(): Function {
    return this.mobileGoBackFuncSource.value;
  }

  changeChatFullAccess(state: boolean): void {
    this.chatFullAccessSource.next(state);
  }

}
